import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { NexsoLogo } from "../../components/app/NexsoLogo";
import { ContactSupportLink } from "../../components/app/ContactSupport";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const bodyFont    = { fontFamily: "Manrope, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

/**
 * Shared shell for `/terms` and `/privacy`. Matches the Nexso sky-blue +
 * white marketing pages so the legal docs feel like part of the product,
 * not a bolted-on afterthought.
 */
export function LegalLayout({ eyebrow, title, accent, lastUpdated, children, testId }) {
  return (
    <div className="min-h-screen text-slate-900 relative overflow-x-hidden" style={{ background: "#FFFFFF", ...bodyFont }} data-testid={testId}>
      {/* Ambient blobs — same as Landing/Download so the pages feel cohesive */}
      <div aria-hidden="true" className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #0EA5E9 0%, transparent 70%)", filter: "blur(80px)" }} />
        <div className="absolute top-[45%] right-[-10%] w-[34rem] h-[34rem] rounded-full opacity-20" style={{ background: "radial-gradient(circle, #38BDF8 0%, transparent 70%)", filter: "blur(80px)" }} />
      </div>

      <div className="relative z-10">
        {/* Top nav — logo + back-to-home */}
        <header className="max-w-4xl mx-auto px-6 md:px-10 pt-8 md:pt-10">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="legal-brand-link">
              <NexsoLogo size="md" />
              <span className="font-semibold text-xl tracking-tight" style={headingFont}>Nexso</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600 transition-colors"
              data-testid="legal-back-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to home
            </Link>
          </div>
        </header>

        {/* Hero */}
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-8">
          <div className="text-xs uppercase tracking-[0.32em] text-sky-600 mb-4" style={headingFont}>{eyebrow}</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold" style={headingFont}>
            {title}{" "}
            {accent && <span style={accentFont} className="text-sky-600">{accent}</span>}
          </h1>
          <p className="mt-5 text-sm text-slate-500">
            Last updated: <span className="text-slate-700 font-medium">{lastUpdated}</span>
          </p>
        </div>

        {/* Body — max-w-3xl for readability, big prose spacing */}
        <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24 legal-prose">
          {children}
        </article>

        {/* Simple footer with cross-link between the two docs */}
        <footer className="max-w-4xl mx-auto px-6 md:px-10 pb-14 border-t pt-8" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-xs text-slate-500">© {new Date().getFullYear()} Nexso. All rights reserved.</div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/terms" className="text-slate-600 hover:text-sky-600 transition-colors" data-testid="legal-footer-terms">Terms of Service</Link>
              <Link to="/privacy" className="text-slate-600 hover:text-sky-600 transition-colors" data-testid="legal-footer-privacy">Privacy Policy</Link>
              <ContactSupportLink className="text-slate-600 hover:text-sky-600 transition-colors" testId="legal-footer-contact">Contact</ContactSupportLink>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * A single section — used inside LegalLayout children. Numbers automatically
 * via CSS counter so authors don't have to renumber when reordering.
 */
export function Section({ id, title, children }) {
  return (
    <section id={id} className="mt-12 first:mt-0 scroll-mt-24" data-testid={`legal-section-${id}`}>
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4 text-slate-900" style={headingFont}>
        {title}
      </h2>
      <div className="space-y-4 text-[15px] leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}
